/** Data-fetching hook for usage metrics endpoints.
 *
 * Uses react-query so data is cached across tab switches.
 * Fetches all 5 time-range metrics plus the plan-distribution snapshot.
 */

import { useCallback, useState } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";

import { apiRequest, isApiError } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";

import type {
  ActiveUsersResponse,
  LoginFrequencyResponse,
  MetricsRange,
  PageDwellTimeResponse,
  PageUsageResponse,
  PlanDistributionResponse,
  UserRetentionResponse,
} from "../lib/metricsTypes";
import { rangeToKey, rangeToQueryParams } from "../lib/metricsTypes";

async function fetchOrEmpty<T>(fn: () => Promise<T>, empty: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (isApiError(err) && err.status === 400) return empty;
    throw err;
  }
}

const EMPTY_ACTIVE_USERS: ActiveUsersResponse = { points: [], summary: { current_dau: 0, current_wau: 0, current_mau: 0 } };
const EMPTY_LOGIN_FREQUENCY: LoginFrequencyResponse = { buckets: [], total_users: 0, avg_logins_per_user: 0 };
const EMPTY_USER_RETENTION: UserRetentionResponse = { cohorts: [], avg_week_1_retention: 0 };
const EMPTY_PAGE_USAGE: PageUsageResponse = { pages: [], total_page_views: 0 };
const EMPTY_PAGE_DWELL: PageDwellTimeResponse = { pages: [], overall_avg_dwell_seconds: 0 };
const EMPTY_PLAN_DIST: PlanDistributionResponse = { plans: [], total_users: 0, trial_users: 0, expired_trial_users: 0, subscribed_users: 0 };

interface UseMetricsResult {
  activeUsers: ActiveUsersResponse | null;
  loginFrequency: LoginFrequencyResponse | null;
  userRetention: UserRetentionResponse | null;
  pageUsage: PageUsageResponse | null;
  pageDwellTime: PageDwellTimeResponse | null;
  planDistribution: PlanDistributionResponse | null;
  loading: boolean;
  error: string | null;
  range: MetricsRange;
  setRange: (range: MetricsRange) => void;
  refetch: () => void;
}

export function useMetrics(initialRange: MetricsRange = "30d"): UseMetricsResult {
  const [range, setRange] = useState<MetricsRange>(initialRange);
  const queryClient = useQueryClient();
  const rangeKey = rangeToKey(range);
  const qp = rangeToQueryParams(range);

  const results = useQueries({
    queries: [
      {
        queryKey: queryKeys.metrics(`active-users:${rangeKey}`),
        queryFn: () => fetchOrEmpty(
          () => apiRequest<ActiveUsersResponse>("GET", `/api/metrics/active-users?${qp}`),
          EMPTY_ACTIVE_USERS,
        ),
        staleTime: 60_000,
      },
      {
        queryKey: queryKeys.metrics(`login-frequency:${rangeKey}`),
        queryFn: () => fetchOrEmpty(
          () => apiRequest<LoginFrequencyResponse>("GET", `/api/metrics/login-frequency?${qp}`),
          EMPTY_LOGIN_FREQUENCY,
        ),
        staleTime: 60_000,
      },
      {
        queryKey: queryKeys.metrics(`user-retention:${rangeKey}`),
        queryFn: () => fetchOrEmpty(
          () => apiRequest<UserRetentionResponse>("GET", `/api/metrics/user-retention?${qp}`),
          EMPTY_USER_RETENTION,
        ),
        staleTime: 60_000,
      },
      {
        queryKey: queryKeys.metrics(`page-usage:${rangeKey}`),
        queryFn: () => fetchOrEmpty(
          () => apiRequest<PageUsageResponse>("GET", `/api/metrics/page-usage?${qp}`),
          EMPTY_PAGE_USAGE,
        ),
        staleTime: 60_000,
      },
      {
        queryKey: queryKeys.metrics(`page-dwell-time:${rangeKey}`),
        queryFn: () => fetchOrEmpty(
          () => apiRequest<PageDwellTimeResponse>("GET", `/api/metrics/page-dwell-time?${qp}`),
          EMPTY_PAGE_DWELL,
        ),
        staleTime: 60_000,
      },
      {
        queryKey: queryKeys.planDistribution(rangeKey),
        queryFn: () => fetchOrEmpty(
          () => apiRequest<PlanDistributionResponse>("GET", `/api/metrics/plan-distribution?${qp}`),
          EMPTY_PLAN_DIST,
        ),
        staleTime: 60_000,
      },
    ],
  });

  const [au, lf, ur, pu, pdt, pd] = results;

  const loading = results.some((r) => r.isLoading);
  const error = results
    .map((r) => (r.error instanceof Error ? r.error.message : null))
    .find(Boolean) ?? null;

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["metrics"] });
  }, [queryClient]);

  return {
    activeUsers: au.data ?? null,
    loginFrequency: lf.data ?? null,
    userRetention: ur.data ?? null,
    pageUsage: pu.data ?? null,
    pageDwellTime: pdt.data ?? null,
    planDistribution: pd.data ?? null,
    loading,
    error,
    range,
    setRange,
    refetch,
  };
}
